import { Subject } from 'rxjs';
import { capitalize } from 'vue';

import SettingWrapper from '@/common/components/controls/table/TableUserSettings/SettingWrapper';
import { TableSettingKeys } from '@/common/components/controls/table/TableUserSettings/TableSettings';
import UserView, { IUserView } from '@/common/components/controls/table/TableUserSettings/UserView';
import { capitalizeTranslation } from '@/globals';
import UserNotifications from '@/utils/UserNotifications';
import i18n from '@/vueI18n';

export default class UserViewService {
  settingWrapper: SettingWrapper<string>;
  views: UserView[] = [];
  views$: Subject<UserView[]> = new Subject<UserView[]>();

  constructor(settingWrapper: SettingWrapper<string>) {
    this.settingWrapper = settingWrapper;
    this.transform();
  }

  transform(): void {
    const value = this.settingWrapper.get() as string;

    // empty string, nothing to do
    if (!value) {
      return;
    }

    try {
      // the new version of views is an array of UserView, try to parse value
      const res: IUserView[] = JSON.parse(value);
      if (!res?.length) return;
      this.views = res.map(
        (view: IUserView) =>
          new UserView({
            name: view.name,
            value: view.value,
            isDefault: view.isDefault,
          }),
      );
    } catch (e) {
      // if parsing doesn't work, it might be the first version of the user views who were only strings
      if (typeof value === 'string') {
        this.views = [
          new UserView({
            name: capitalizeTranslation('names.default'),
            value,
            isDefault: true,
          }),
        ];
      } else {
        // houston we have a problem
        throw e;
      }
    }
  }

  applyView(view: UserView | null): void {
    const queryParams = new URLSearchParams(window.location.search);

    if (view) {
      this.views = this.views.map((v) => {
        v.isApplied = v.name === view.name;
        return v;
      });
      queryParams.set(TableSettingKeys.Filters, view?.value);
    } else {
      queryParams.delete(TableSettingKeys.Filters);
    }

    history.replaceState(null, null, `?${queryParams.toString()}`);
  }

  clearView(): void {
    this.views = this.views.map((view) => {
      view.isApplied = false;
      return view;
    });
  }

  async add(value: UserView, apply = true): Promise<void> {
    // check the uniqueness of the new view name
    if (this.views.find((view) => value.name === view.name)) {
      UserNotifications.somethingWentWrong(null, `${i18n.global.t('messages.viewWithSameName')} ${value.name}`);
      return;
    }

    this.views.push(value);
    this.views = this.views.map((view) => {
      // only one view can be default
      if (value.isDefault) {
        view.isDefault = view.name === value.name;
      }
      return view;
    });

    await this.saveUserSetting();

    if (apply) {
      this.applyView(value);
    }

    this.updateViews();
  }

  async rename(value: UserView, newName: string): Promise<void> {
    this.views = this.views.map((view) => {
      if (view.name === value.name) {
        view.name = newName;
        return view;
      } else {
        return view;
      }
    });
    await this.saveUserSetting();
    this.updateViews();
  }

  async update(updatedView: UserView): Promise<void> {
    // if user is trying to update an empty filter, remove it
    if (!updatedView.value) {
      await this.remove(updatedView);
      return;
    }

    this.views = this.views.map((view) => {
      if (view.name === updatedView.name) {
        return updatedView;
      }
      return view;
    });

    await this.saveUserSetting();
    this.updateViews();
  }

  async remove(value: UserView): Promise<void> {
    this.views = this.views.filter((view) => view.name !== value.name);
    await this.saveUserSetting();
    this.updateViews();
  }

  async removeList(values: UserView[]): Promise<void> {
    const toRemove = values.map((value) => value.name);
    this.views = this.views.filter((view) => !toRemove.includes(view.name));
    await this.saveUserSetting();
    this.updateViews();
  }

  async markAsDefault(value: UserView): Promise<void> {
    this.views = this.views.map((view) => {
      if (view.name === value.name) {
        view.isDefault = !value.isDefault;
      } else {
        view.isDefault = false;
      }
      return view;
    });
    await this.saveUserSetting();
    this.updateViews();
  }

  getViewFromUrl(): UserView {
    const queryParams = new URLSearchParams(window.location.search).get(TableSettingKeys.Filters);
    return this.views?.find((view) => view.value === queryParams);
  }

  private async saveUserSetting() {
    const serializedFilters = JSON.stringify(this.views);
    await this.settingWrapper.set(serializedFilters as Partial<string>);

    UserNotifications.success({
      title: `${capitalize(i18n.global.t('messages.allGood', { n: 1 }))} 👍`,
      message: i18n.global.t('messages.defaultFiltersSavedSuccessfully'),
    });
  }

  private updateViews(): void {
    this.views$.next(this.views);
  }
}
